import { useCallback, useEffect, useState } from "react";

/**
 * Global Cmd/Ctrl+K command palette shortcut hook.
 *
 * Registers a `keydown` listener on `window` that toggles the palette open
 * when the user presses Cmd+K (macOS) or Ctrl+K (Windows/Linux).  The
 * browser's native Cmd+K (which focuses the URL/search bar in some
 * browsers) is suppressed via `preventDefault()`.
 *
 * Escape-to-close is handled natively by the Radix Dialog underlying
 * `CommandDialog`, so this hook does not need to listen for it.
 */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd+K (macOS) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  return { isOpen, open, close, toggle };
}
